// Screen frame (R-U7, R-U17, um/2026-10-30/03 п.1, п.3, п.10, um/2026-10-30/04): the icon rail → the left column that folds and is dragged wider (one for every
// section: the tree, the settings, or what a section puts there, as the mail folders; its head names what is open, the plate of the
// event sits at its bottom in a section the event narrows) → the content with its own top line (ui/TopBar). The search is in the top
// line and ⌘K, the state of the sync sits on the rail.

import { AnalyticsScreen } from '../analytics/AnalyticsScreen.tsx';
import { useEffect, useRef, useState } from 'react';
import { ColumnFoot, FootScreen, ScopePlate, scopeField, useScope, useScopeSync, useScopeWords, withScope } from './scope.tsx';
import { recordHomeOf, registerRecordTabs, registerScreens, registerSectionNav, screenOf, sectionNavOf, typeEmptyOf, type SectionNav } from './screens.ts';
import { AccountScreen } from '../settings/AccountScreen.tsx';
import { DomainsTab } from '../sites/DomainsTab.tsx';
import { Builder, OpenInBuilder } from '../sites/Builder.tsx';
import { FormsScreen } from '../forms/FormsScreen.tsx';
import { FilesScreen } from '../files/FilesScreen.tsx';
import { FilesNav, useFilesTitle } from '../files/FilesNav.tsx';
import { canRead, menuPlace, menuScreens, menuTypeIds, menuViews, type Access, type MenuSection, type TypeDef } from '@confdeck/core-shared';
import { AutomationsScreen } from '../automations/AutomationsScreen.tsx';
import { AutomationsNav, useAutomationsTitle } from '../automations/AutomationsNav.tsx';
import { PipelineScreen } from '../pipelines/PipelineScreen.tsx';
import { MineScreen } from '../pipelines/MineScreen.tsx';
import { pipelineHref, pipelineSectionOf, pipelineType } from '../pipelines/pipeline-section.ts';
import { RecordView } from '../record/RecordView.tsx';
import { goBack } from '../record/RecordPath.tsx';
import { prefetchQuery, useAccess, useMeta, useRecord, useSync } from '../sync/react.tsx';
import { canWrite } from '@confdeck/core-shared';
import { Button, cx, useNarrow } from '../ui/primitives.tsx';
import { CommandPalette } from './CommandPalette.tsx';
import { answerLeave, useLeaveQuestion } from './unsaved.ts';
import { onOpenSearch } from './screen-search.ts';
import { LeaveDialog } from '../ui/LeaveDialog.tsx';
import { TopBar, TopNav, TopTitle } from '../ui/TopBar.tsx';
import { cameFromApp, navigate, parseRoute, recordHref, typeHref, useLocation } from './router.ts';
import { TypeScreen } from './TypeScreen.tsx';
import { CardStack } from './CardStack.tsx';
import { openCard } from './cards.ts';
import { Rail } from './Rail.tsx';
import { CreateType, SideNav } from './SideNav.tsx';
import { litItem, navLists, type NavActive } from './nav-tree.ts';
import { termField } from './nav-terms.ts';
import { listOf, sectionHref } from './nav-place.ts';
import { SettingsNav, SettingsPage, movedScreen, useSettingsTitle } from '../settings/SettingsArea.tsx';
import { UiKit } from '../ui/UiKit.tsx';
import { ColResizer, SIDE_W } from '../ui/ColWidth.tsx';
import s from './shell.module.css';

// Screens of the core itself; apps add theirs the same way. People, roles, the structure and the settings are pages of «Настройки».
registerScreens({ forms: FormsScreen, files: FilesScreen, builder: Builder, account: AccountScreen, 'ui-kit': UiKit, analytics: AnalyticsScreen });
// «Файлы» choose the section of the files in the left column (um/2026-10-30/03 п.1).
registerSectionNav('files', { nav: FilesNav, useTitle: useFilesTitle });
// «Автоматизации» are not a rail section of records: their column holds the categories of scenarios (um/2026-10-30/03 п.9).
registerSectionNav('automations', { nav: AutomationsNav, useTitle: useAutomationsTitle });
// Decision 023: a page of the site is edited as blocks.
registerRecordTabs('page', [{ key: 'builder', label: 'Конструктор', component: OpenInBuilder }]);
registerRecordTabs('site', [{ key: 'domains', label: 'Домены', component: DomainsTab }]);

/** The column of «Автоматизации»: a section of its own, behind the rail button, not one of the menu. */
const AUTOMATIONS: MenuSection = { key: 'automations', label: 'Автоматизации', icon: 'zap', typeIds: [] };

function sectionsWithReference(menu: MenuSection[], types: TypeDef[], access: Access): MenuSection[] {
  // «Справочники» always lists the types people made in the interface, also ones created after the snapshot — those the person
  // may read, as the server filters them (review nav, P3-1). Empty, it opens its start page with «Новый справочник» (um/2026-10-30/03 п.1).
  return menu.map((m) => {
    if (m.key !== 'reference') return m;
    const typeIds = types.filter((t) => !t.app && canRead(access, t.id)).map((t) => t.id);
    return { ...m, typeIds, ...(typeIds.length ? {} : { start: REFERENCE_START }) };
  });
}

export function Shell({ onLogout }: { onLogout: () => void }) {
  useScopeSync();
  const client = useSync();
  const meta = useMeta();
  const { path, search } = useLocation();
  const route = parseRoute(path, search);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('confdeck:sidebar') === 'collapsed');
  const [peek, setPeek] = useState(false);
  const railRef = useRef<HTMLElement>(null);
  const sideRef = useRef<HTMLElement>(null);
  const [drawer, setDrawer] = useState(false);
  const narrow = useNarrow();
  const [palette, setPalette] = useState(false);
  // The rail has the sections of work; those of «Настройки» open from the avatar (decision 031 п.3).
  const access = useAccess();
  // An empty «Справочники» is on the rail of the owner only: they are the one who makes a section there (R-M2).
  const isOwner = meta.user(client.header?.userId)?.role === 'owner';
  const sections = sectionsWithReference(client.menu, meta.types(), access).filter((m) => !m.settings && (m.key !== 'reference' || m.typeIds.length || isOwner));

  // Read through the store's ticks: on a direct visit the shell shows before the snapshot is in, and the record comes later.
  const record = useRecord(route.name === 'record' ? route.recordId : null);
  const current = route.name === 'type' || route.name === 'mine' ? meta.typeByKey(route.typeKey) : meta.type(record?.typeId);
  // A section that lives in pipelines lights its own items (R-U29): «Мои», a branch of «Все», a pipeline.
  const branch = route.name === 'type' && pipelineSectionOf(current?.key)?.branches?.some((b) => b.key === search.get('branch')) ? `branch:${search.get('branch')}` : undefined;
  const active: NavActive = route.name === 'screen' ? { screen: route.key }
    : route.name === 'pipeline' ? { typeId: pipelineType(meta, route.id), extra: route.id }
    : route.name === 'mine' ? { typeId: current?.id, extra: 'mine' }
    : branch ? { typeId: current?.id, viewId: route.name === 'type' ? route.viewId : null, extra: branch }
    // A value of a field put into the column (?term=) is the lit item of the section (um/2026-10-30/03 п.7).
    // Only a value that narrows the list counts: a stray ?term= of no field in the column lights and names nothing.
    : { typeId: current?.id, viewId: route.name === 'type' ? route.viewId : null, extra: route.name === 'type' && current && termField(meta, current.id, search.get('term')) ? search.get('term')! : undefined };
  const here = route.name === 'automations' ? 'automations' : route.name === 'settings' ? 'settings' : null;
  const section = here ? undefined : menuPlace(sections, (n) => navLists(n, active))?.section
    ?? (route.name === 'screen' ? sections.find((m) => m.start === route.key) : undefined) ?? sections[0];

  // The start page is the first page of the first section: «Билеты» of the chosen event, not the list of conferences. Re-checked
  // when the menu and the schema arrive, so a role that logs in before the snapshot does not stay on a blank page (Grok audit, round 2).
  const loaded = client.timings.loaded !== undefined;
  // A new account has no events yet: the start is the app's first step (the empty section with its onboarding, plan N1), not an empty
  // «Билеты» (Antigravity checkpoint 30.10). Only after the data is in — a cold start must not take the demo there.
  const scopeType = client.header?.scope?.typeId;
  const firstRun = loaded && scopeType && client.store.count(scopeType) === 0
    ? sections.flatMap((m) => menuTypeIds(m)).map((tid) => meta.type(tid)).find((t) => t && typeEmptyOf(t.key) && canWrite(access, t.id))
    : undefined;
  const startHref = (firstRun ? typeHref(firstRun.key) : null) ?? sections.map((m) => sectionHref(m, meta)).find(Boolean) ?? null;
  useEffect(() => {
    // Only the bare address goes to the start section; an unknown address says so (audit 28.10, P1-14).
    if (route.name === 'home' && startHref && loaded) navigate(startHref, { replace: true });
    // Old addresses keep working: a record of the former side panel is its page, a former rail screen is its item of «Настройки».
    if (route.name === 'record' && route.moved) navigate(recordHref(route.recordId), { replace: true });
    if (route.name === 'pipeline' && route.moved) navigate(pipelineHref(route.id, 'settings'), { replace: true });
    const moved = route.name === 'screen' ? movedScreen(route.key) : undefined;
    if (moved) navigate(search.size ? `${moved}?${search}` : moved, { replace: true }); // the query (a payment coming back) goes along
    // A record that lives on a screen of its own (a letter in the mail client) opens there, not as a page.
    const home = record && recordHomeOf(meta.type(record.typeId)?.key);
    if (home) navigate(home(record!.id), { replace: true });
  }, [path, startHref, loaded, record?.typeId]);
  // After the snapshot is in, the views a click opens are prepared in idle time — with the filter the screen builds: narrowed to the
  // chosen event in a section of events, `$scope` bound (MiniMax review 30.10, P2-5). Another choice of the event warms them again.
  const [scopeId] = useScope();
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  useEffect(() => {
    let cancelled = false;
    const idle = (cb: () => void) => ((window as unknown as { requestIdleCallback?: (c: () => void) => void }).requestIdleCallback ?? ((c: () => void) => setTimeout(c, 200)))(cb);
    const warm = () => {
      if (cancelled) return;
      if (client.timings.loaded === undefined) { setTimeout(warm, 500); return; }
      const scopeType = client.header?.scope?.typeId;
      const scoped = new Set<string>(sectionsRef.current.filter((m) => m.eventScoped).flatMap((m) => [...menuTypeIds(m), ...menuViews(m).map((v) => v.typeId)]));
      const pointed = sectionsRef.current.flatMap((m) => menuViews(m).map((v) => meta.view(v.viewId)));
      const views = [...meta.types().map((t) => meta.views(t.id)[0]), ...pointed].filter((v) => !!v);
      const next = () => {
        const v = views.shift();
        if (!v || cancelled) return;
        const sField = scoped.has(v.typeId) ? scopeField(meta, v.typeId, scopeType) : undefined;
        prefetchQuery(client, v.typeId, withScope(v.filter, sField, scopeId), v.sort);
        idle(next);
      };
      idle(next);
    };
    warm();
    return () => { cancelled = true; };
  }, [client, scopeId]);

  // A screen's own key for the search («/» of the mail) opens the same palette (R-U23).
  useEffect(() => onOpenSearch(() => setPalette(true)), []);
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPalette((p) => !p); }
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') { e.preventDefault(); toggleSidebar(); }
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  });

  const toggleSidebar = () => {
    // На узком экране второе меню — выдвижная панель: её открывает кнопка рельса.
    if (narrow) { setDrawer((d) => !d); setPeek(false); return; }
    const next = !collapsed;
    setCollapsed(next);
    setPeek(false);
    try { localStorage.setItem('confdeck:sidebar', next ? 'collapsed' : 'open'); } catch { /* convenience only */ }
  };

  const floating = collapsed;
  const navSection = route.name === 'automations' ? AUTOMATIONS : section;
  const nav = route.name === 'settings' ? undefined : sectionNavOf(navSection?.key);
  // На узком экране открытая панель закрывается при переходе на другой экран.
  useEffect(() => { if (narrow) setDrawer(false); }, [path, narrow]);
  // The peek lasts while the pointer is on the rail or on the menu itself; moving from one to the other keeps it.
  const peekCtl = { show: () => { if (floating && !narrow) setPeek(true); }, leave: (to: EventTarget | null) => {
    if (!(to instanceof Node && (railRef.current?.contains(to) || sideRef.current?.contains(to)))) setPeek(false);
  } };
  // «Назад» after a direct visit leads to the record's list; while the record is still loading it waits for it (review nav, P2-2).
  const [backPending, setBackPending] = useState(false);
  const back = () => {
    // Read the store now, not the record of the last render: the snapshot may have just brought it in.
    const rec = route.name === 'record' ? client.store.record(route.recordId) : undefined;
    const t = meta.type(rec?.typeId);
    if (!t && !cameFromApp() && client.timings.loaded === undefined) { setBackPending(true); return; }
    setBackPending(false);
    goBack(t ? listOf(t.id) ?? typeHref(t.key) : null);
  };
  useEffect(() => { if (backPending && (record || client.timings.loaded !== undefined)) back(); }, [backPending, record?.typeId, client.timings.loaded]);
  useEffect(() => { setBackPending(false); }, [path]);

  return (
    <div className={cx(s.app, floating && s.collapsed)}>
      <Rail navRef={railRef} peek={peekCtl} sections={sections} section={section} here={here} onLogout={onLogout} onSearch={() => setPalette(true)}
        onOpen={(m) => { const h = sectionHref(m, meta); if (h) navigate(h); }}
        sidebar={{ label: narrow ? (drawer ? 'Закрыть меню' : 'Показать меню') : floating ? 'Показать меню' : 'Скрыть меню', toggle: toggleSidebar }} />
      <aside className={cx(s.side, floating && s.sideFloat, floating && peek && s.sidePeek, narrow && drawer && s.sideOpen)} ref={sideRef} onMouseLeave={(e) => peekCtl.leave(e.relatedTarget)} aria-label="Подразделы">
        <div className={s.sideHead}>
          <ColumnTitle key={route.name === 'settings' ? 'settings' : navSection?.key} nav={nav} fallback={
            route.name === 'settings' ? <SettingsTitle section={route.section} /> : <span className={s.sideTitle}>{section ? itemTitle(section, active, meta, current?.nameMany) : ''}</span>} />
        </div>
        <div className={s.sideList}>
          {route.name === 'settings' ? <SettingsNav section={route.section} /> : nav && navSection ? <nav.nav section={navSection} active={active} /> : section && <SideNav section={section} active={active} />}
          {route.name !== 'settings' && section?.key === 'reference' && !section.typeIds.length && <div className={s.sideEmpty}>Здесь появятся справочники, которые вы создадите</div>}
          {route.name !== 'settings' && section?.key === 'reference' && <CreateType />}
        </div>
        {/* The event is chosen in one place, the same in every section it narrows (um/2026-10-30/04 п.2). */}
        {/* The bottom of the column is one slot: the section's own action (the mail's «Новое письмо», um/2026-10-31/02) or the event, */}
        {/* with the screens fixed above it (R-U7, um/2026-10-31/03: «Вход на событие»). */}
        {route.name !== 'settings' && (nav?.foot ? <ColumnFoot><nav.foot /></ColumnFoot> : navSection && <SectionFoot section={navSection} active={active} />)}
        <WorkMark />
      </aside>
      {/* One width of the column for every section (R-U17): dragged here, the same in all of them and after a reload. */}
      {!floating && !narrow && <ColResizer width={SIDE_W} label="Ширина меню" className={s.sideEdge} />}
      {narrow && drawer && <button type="button" className={s.sideBackdrop} aria-label="Закрыть меню" onClick={() => setDrawer(false)} />}
      <main className={s.main}>
        <TopNav.Provider value={narrow ? () => { setDrawer(true); setPeek(false); } : null}>
        <div className={s.content}>
          {route.name === 'automations' && <AutomationsScreen id={route.id} />}
          {route.name === 'screen' && route.key === REFERENCE_START && isOwner && <ReferenceStart />}
          {route.name === 'screen' && route.key !== REFERENCE_START && !movedScreen(route.key) && <ScreenHost k={route.key} />}
          {route.name === 'settings' && <SettingsPage section={route.section} />}
          {route.name === 'pipeline' && !route.moved && <PipelineScreen key={route.id} pipelineId={route.id} tab={route.tab} />}
          {route.name === 'mine' && <MineScreen key={route.typeKey} typeKey={route.typeKey} layout={route.layout} />}
          {/* A person without roles sees no sections (П1, R-A2): the page says why instead of staying blank. */}
          {route.name === 'notFound' && <div className={s.empty}><div>Страница не найдена. Проверьте адрес или откройте раздел из меню.</div>{startHref && <Button variant="secondary" onClick={() => navigate(startHref)}>На главную</Button>}</div>}
          {route.name === 'home' && !startHref && (
            <div className={s.empty}>У вас пока нет ролей в этом пространстве. Разделы появятся, когда администратор назначит вам роль.</div>
          )}
          {route.name === 'type' && <TypeScreen typeKey={route.typeKey} viewId={route.viewId} scoped={!!section?.eventScoped} />}
          {route.name === 'record' && !route.moved && (
            <div className={s.recordPage}>
              <RecordView key={route.recordId} id={route.recordId} onClose={back} onOpen={openCard} />
            </div>
          )}
        </div>
        </TopNav.Provider>
      </main>
      {/* Cards linked from a single lie over the screen (R-U19); the stack is in the address, another path leaves it behind. */}
      <CardStack />
      <CommandPalette open={palette} onClose={() => setPalette(false)} />
      <UnsavedQuestion />
    </div>
  );
}

/** The bottom of a section's column: its fixed screens, then the plate of the event in a section the event narrows. */
function SectionFoot({ section, active }: { section: MenuSection; active: NavActive }) {
  // A screen the chosen event has not (R-E9: the entrance of a product without a date) is not at the bottom either.
  const words = useScopeWords();
  const fixed = menuScreens(section).filter((sc) => sc.foot && !(section.eventScoped && words.screens.has(sc.key)));
  if (!fixed.length && !section.eventScoped) return null;
  return (
    <ColumnFoot>
      {fixed.map((sc) => <FootScreen key={sc.key} icon={sc.icon} label={sc.label} hint={sc.hint} on={active.screen === sc.key} onClick={() => navigate(`/s/${sc.key}`)} />)}
      {section.eventScoped && <ScopePlate />}
    </ColumnFoot>
  );
}

/** The head of the column names the entity the person is on (um/2026-10-30/03 п.1): the open view, section of records or screen. */
function itemTitle(section: MenuSection, active: NavActive, meta: ReturnType<typeof useMeta>, typeName: string | undefined): string {
  const lit = litItem(section, active);
  if (lit.kind === 'view') return menuViews(section).find((v) => v.viewId === lit.id)?.label ?? '';
  if (lit.kind === 'screen') return menuScreens(section).find((x) => x.key === lit.id)?.label ?? section.label;
  if (lit.kind === 'type') return pipelineSectionOf(meta.type(lit.id)?.key)?.words.all ?? meta.type(lit.id)?.nameMany ?? typeName ?? section.label;
  if (lit.kind === 'extra') {
    const piped = pipelineSectionOf(meta.type(active.typeId)?.key);
    if (piped && lit.id === 'mine') return piped.words.mine;
    if (piped && lit.id?.startsWith('branch:')) return piped.branches?.find((b) => `branch:${b.key}` === lit.id)?.label ?? piped.words.all;
    return meta.term(lit.id)?.name ?? typeName ?? section.label;
  }
  return section.label;
}

/** A section with a column of its own names the entity itself (the folder of the mail); keyed by the section, so its hook stays put. */
function ColumnTitle({ nav, fallback }: { nav: SectionNav | undefined; fallback: React.ReactNode }) {
  return nav ? <OwnTitle useTitle={nav.useTitle} fallback={fallback} /> : <>{fallback}</>;
}
/** The hook of the section's column is called on every render of its own component (keyed by the section), never conditionally. */
function OwnTitle({ useTitle, fallback }: { useTitle: () => string | null; fallback: React.ReactNode }) {
  const own = useTitle();
  return own ? <span className={s.sideTitle}>{own}</span> : <>{fallback}</>;
}
function SettingsTitle({ section }: { section: string | null }) {
  return <span className={s.sideTitle}>{useSettingsTitle(section)}</span>;
}

/** An external person (R-A9) sees whose shell this is and the way back to their personal page. */
function WorkMark() {
  const client = useSync();
  const me = useMeta().user(client.header?.userId);
  if (!me?.external) return null;
  return <div className={s.sideFoot}><span className={s.workMark}>Рабочие разделы{client.header?.siteUrl && <a className={s.workBack} href={`${client.header.siteUrl}/login`}>Личный кабинет</a>}</span></div>;
}

/** The start page of an empty «Справочники»: nothing yet, and the one button that makes the first one. */
const REFERENCE_START = 'reference';
function ReferenceStart() {
  return (
    <>
      <TopBar start={<TopTitle icon="book">Справочники</TopTitle>} />
      <div className={s.empty}><div>Справочников пока нет. Сделайте первый — например, «Площадки» или «Подрядчики».</div><CreateType big /></div>
    </>
  );
}

function ScreenHost({ k }: { k: string }) {
  const client = useSync();
  // A screen opens only from the person's own menu (F4 audit №1): a typed address does not show «Команда» to a volunteer.
  // The showcase of the controls is the owner's hidden page: it is in no menu (DESIGN.md «Контролы»).
  const owner = useMeta().user(client.header?.userId)?.role === 'owner';
  const mine = client.menu.some((m) => menuScreens(m).some((sc) => sc.key === k)) || (k === 'ui-kit' && owner);
  const C = mine ? screenOf(k) : undefined;
  return C ? <C /> : <div className={s.empty}>Экран не найден</div>;
}

/** The question before leaving unsaved work, one for every screen of the frame (shell/unsaved.ts). */
function UnsavedQuestion() {
  const q = useLeaveQuestion();
  return q ? <LeaveDialog what={q.what} saving={q.saving} onAnswer={answerLeave} /> : null;
}
